import { FC, useMemo, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import {
  giftDeliveryCategoryListDataAtom,
  giftDeliveryCategoryListStatusAtom,
} from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.atom';
import { useGiftDeliveryCategoryListHook } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.hook';
import {
  giftItemBrandListDataAtom,
  giftItemBrandListStatusAtom,
} from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import {
  giftItemInfoBrandFilterAtom,
  giftItemInfoDeliveryCategoryFilterAtom,
  giftItemInfoListDataAtom,
  giftItemInfoListStatusAtom,
  giftItemInfoSearchQueryAtom,
  GiftItemInfoSortType,
  giftItemInfoSortTypeAtom,
  GiftItemInfoStatusFilter,
  giftItemInfoStatusFilterAtom,
  giftItemInfoSubCategoryFilterAtom,
  GiftItemInfoTaxFilter,
  giftItemInfoTaxFilterAtom,
  GiftItemInfoTypeFilter,
  giftItemInfoTypeFilterAtom,
} from '@shared/api/giftItemInfo/giftItemInfo.atom';
import { bulkSetGiftItemInfoEnabled } from '@shared/api/giftItemInfo/giftItemInfo.controller';
import { useGiftItemInfoListHook } from '@shared/api/giftItemInfo/giftItemInfo.hook';
import {
  GiftItemInfoData,
  GiftItemInfoType,
} from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { invalidateQueriesByUrlPart } from '@shared/hooks/recoil-query';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';

export const GiftItemInfoListPage: FC = function GiftItemInfoListPage() {
  const { sellerRole } = useAuth();
  const queryClient = useQueryClient();

  useGiftItemInfoListHook(sellerRole());
  useGiftItemBrandListHook(sellerRole());
  useGiftDeliveryCategoryListHook(sellerRole());

  const status = useRecoilValue(giftItemInfoListStatusAtom);
  const data = useRecoilValue(giftItemInfoListDataAtom);

  // 브랜드 목록
  const brandListStatus = useRecoilValue(giftItemBrandListStatusAtom);
  const brandList = useRecoilValue(giftItemBrandListDataAtom);

  // 배송 카테고리 목록
  const deliveryCategoryListStatus = useRecoilValue(giftDeliveryCategoryListStatusAtom);
  const deliveryCategoryList = useRecoilValue(giftDeliveryCategoryListDataAtom);

  // 필터 및 검색 상태 (Recoil로 관리하여 페이지 이동 후에도 유지)
  const [searchQuery, setSearchQuery] = useRecoilState(giftItemInfoSearchQueryAtom);
  const [typeFilter, setTypeFilter] = useRecoilState(giftItemInfoTypeFilterAtom);
  const [statusFilter, setStatusFilter] = useRecoilState(giftItemInfoStatusFilterAtom);
  const [brandFilter, setBrandFilter] = useRecoilState(giftItemInfoBrandFilterAtom);
  const [deliveryCategoryFilter, setDeliveryCategoryFilter] = useRecoilState(
    giftItemInfoDeliveryCategoryFilterAtom,
  );
  const [subCategoryFilter, setSubCategoryFilter] = useRecoilState(
    giftItemInfoSubCategoryFilterAtom,
  );
  const [taxFilter, setTaxFilter] = useRecoilState(giftItemInfoTaxFilterAtom);
  const [sortType, setSortType] = useRecoilState(giftItemInfoSortTypeAtom);

  // 타입 필터가 배송형이 아닐 때만 배송 카테고리 필터가 의미를 가지므로,
  // 배송형이 아니면 배송 카테고리 필터를 초기화한다.
  const handleTypeFilterChange = (value: GiftItemInfoTypeFilter) => {
    setTypeFilter(value);
    if (value !== 'Delivery') {
      setDeliveryCategoryFilter('all');
      setSubCategoryFilter('all');
    }
  };

  // 배송 카테고리가 바뀌면 하위분류 필터는 초기화 (카테고리마다 하위분류가 다름)
  const handleDeliveryCategoryFilterChange = (value: string) => {
    setDeliveryCategoryFilter(value);
    setSubCategoryFilter('all');
  };

  const getImageUrl = (item: GiftItemInfoData): string => {
    const image = item.image;
    if (image && typeof image === 'object' && 'url' in image) {
      return fileBucketUrl((image as Image).url, 'raw');
    }
    return '';
  };

  // 필터링 및 정렬된 데이터
  const filteredAndSortedData = useMemo(() => {
    if (!data) return [];

    let result = [...data];

    // 1. 검색 필터 (이름, 태그, 상품코드)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(
        (item) =>
          item.name.toLowerCase().includes(query) ||
          item.tags?.some((tag) => tag.toLowerCase().includes(query)) ||
          (item.productCode != null && String(item.productCode).includes(query)),
      );
    }

    // 2. 타입 필터
    if (typeFilter !== 'all') {
      result = result.filter((item) => item.type === typeFilter);
    }

    // 3. 활성화 상태 필터
    if (statusFilter !== 'all') {
      result = result.filter((item) =>
        statusFilter === 'enabled' ? item.isEnabled : !item.isEnabled,
      );
    }

    // 4. 과세여부 필터
    if (taxFilter !== 'all') {
      result = result.filter((item) =>
        taxFilter === 'taxable' ? item.isTaxable !== false : item.isTaxable === false,
      );
    }

    // 5. 브랜드 필터
    if (brandFilter !== 'all') {
      if (brandFilter === 'none') {
        // 브랜드 없는 상품
        result = result.filter((item) => !item.giftItemBrand);
      } else {
        result = result.filter((item) => {
          const brandId =
            typeof item.giftItemBrand === 'object'
              ? item.giftItemBrand?._id
              : item.giftItemBrand;
          return brandId === brandFilter;
        });
      }
    }

    // 6. 배송 카테고리 필터 (배송형 상품에만 적용)
    if (deliveryCategoryFilter !== 'all') {
      if (deliveryCategoryFilter === 'none') {
        // 배송 카테고리가 지정되지 않은 상품
        result = result.filter(
          (item) => item.type === GiftItemInfoType.Delivery && !item.giftDeliveryCategory,
        );
      } else {
        result = result.filter((item) => {
          if (item.type !== GiftItemInfoType.Delivery) return false;
          const categoryId =
            typeof item.giftDeliveryCategory === 'object'
              ? item.giftDeliveryCategory?._id
              : item.giftDeliveryCategory;
          return categoryId === deliveryCategoryFilter;
        });
      }
    }

    // 6-1. 하위분류 필터 (배송형 상품에만 적용)
    if (subCategoryFilter !== 'all') {
      if (subCategoryFilter === 'none') {
        result = result.filter(
          (item) => item.type === GiftItemInfoType.Delivery && !item.subCategory,
        );
      } else {
        result = result.filter(
          (item) =>
            item.type === GiftItemInfoType.Delivery &&
            item.subCategory === subCategoryFilter,
        );
      }
    }

    // 7. 정렬
    switch (sortType) {
      case 'order':
        result.sort((a, b) => {
          const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
          const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });
        break;
      case 'name-asc':
        result.sort((a, b) => a.name.localeCompare(b.name, 'ko'));
        break;
      case 'name-desc':
        result.sort((a, b) => b.name.localeCompare(a.name, 'ko'));
        break;
      case 'price-asc':
        result.sort((a, b) => (a.priceWhole || 0) - (b.priceWhole || 0));
        break;
      case 'price-desc':
        result.sort((a, b) => (b.priceWhole || 0) - (a.priceWhole || 0));
        break;
      case 'latest':
        result.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        break;
      case 'oldest':
        result.sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
        break;
    }

    return result;
  }, [
    data,
    searchQuery,
    typeFilter,
    statusFilter,
    taxFilter,
    brandFilter,
    deliveryCategoryFilter,
    subCategoryFilter,
    sortType,
  ]);

  // 마진율(%) 계산: (판매가 - 도매가) / 판매가 * 100
  const getMarginRate = (item: GiftItemInfoData): number | null => {
    if (!item.priceRetail || !item.priceWhole) return null;
    return ((item.priceRetail - item.priceWhole) / item.priceRetail) * 100;
  };

  // 같은 하위분류 중 마진율이 가장 높은 상품의 _id 집합
  // (배송 카테고리 + 하위분류명으로 그룹화)
  const topMarginIdSet = useMemo(() => {
    const set = new Set<string>();
    if (!data) return set;

    const bestByGroup = new Map<string, { id: string; rate: number }>();
    for (const item of data) {
      if (!item.subCategory) continue;
      const rate = getMarginRate(item);
      if (rate === null) continue;
      const categoryId =
        typeof item.giftDeliveryCategory === 'object'
          ? item.giftDeliveryCategory?._id
          : item.giftDeliveryCategory;
      const groupKey = `${categoryId ?? 'none'}::${item.subCategory}`;
      const best = bestByGroup.get(groupKey);
      if (!best || rate > best.rate) {
        bestByGroup.set(groupKey, { id: item._id, rate });
      }
    }
    Array.from(bestByGroup.values()).forEach(({ id }) => set.add(id));
    return set;
  }, [data]);

  // 하위분류 필터 옵션 목록
  // 특정 배송 카테고리가 선택되면 해당 카테고리의 subCategories 기준,
  // 그렇지 않으면 전체 배송형 상품에서 사용 중인 하위분류를 수집한다.
  const availableSubCategories = useMemo(() => {
    if (deliveryCategoryFilter !== 'all' && deliveryCategoryFilter !== 'none') {
      const category = deliveryCategoryList.find((c) => c._id === deliveryCategoryFilter);
      if (category?.subCategories?.length) {
        return [...category.subCategories];
      }
    }
    if (!data) return [];
    const set = new Set<string>();
    for (const item of data) {
      if (item.type === GiftItemInfoType.Delivery && item.subCategory) {
        set.add(item.subCategory);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ko'));
  }, [data, deliveryCategoryFilter, deliveryCategoryList]);

  // 통계 정보
  const stats = useMemo(() => {
    if (!data) return { total: 0, delivery: 0, coupon: 0, enabled: 0, disabled: 0 };
    return {
      total: data.length,
      delivery: data.filter((item) => item.type === GiftItemInfoType.Delivery).length,
      coupon: data.filter((item) => item.type === GiftItemInfoType.Coupon).length,
      enabled: data.filter((item) => item.isEnabled).length,
      disabled: data.filter((item) => !item.isEnabled).length,
    };
  }, [data]);

  const itemListRender = useMemo(() => {
    if (status === 'success') {
      if (filteredAndSortedData.length === 0) {
        return (
          <div className="py-8 text-center text-gray-500">
            {searchQuery ||
            typeFilter !== 'all' ||
            statusFilter !== 'all' ||
            taxFilter !== 'all' ||
            brandFilter !== 'all' ||
            deliveryCategoryFilter !== 'all' ||
            subCategoryFilter !== 'all'
              ? '검색 결과가 없습니다.'
              : '등록된 원상품이 없습니다.'}
          </div>
        );
      }

      return (
        <div className="space-y-2">
          {filteredAndSortedData.map((item) => (
            <Link
              key={item._id}
              to={`/gift-seller/item-info/${item._id}/edit`}
              className={`flex items-center gap-3 rounded-lg border p-3 ${
                item.isEnabled
                  ? 'bg-white hover:bg-gray-50'
                  : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
              }`}
            >
              <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                {getImageUrl(item) ? (
                  <img
                    src={getImageUrl(item)}
                    alt={item.name}
                    className={`h-full w-full object-cover ${
                      item.isEnabled ? '' : 'opacity-60 grayscale'
                    }`}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                    No Image
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  {/* 상품코드 뱃지 */}
                  {item.productCode != null && (
                    <span className="rounded bg-sky-100 px-1.5 py-0.5 text-xs font-medium text-sky-700">
                      코드: {item.productCode}
                    </span>
                  )}
                  {/* 표시순서 뱃지 */}
                  {item.order !== undefined && (
                    <span className="rounded bg-gray-700 px-1.5 py-0.5 text-xs font-medium text-white">
                      #{item.order}
                    </span>
                  )}
                  {/* 브랜드 뱃지 */}
                  {item.giftItemBrand && typeof item.giftItemBrand === 'object' && (
                    <span className="rounded bg-orange-100 px-1.5 py-0.5 text-xs font-medium text-orange-600">
                      {item.giftItemBrand.name}
                    </span>
                  )}
                  <span
                    className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                      item.type === GiftItemInfoType.Delivery
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-purple-100 text-purple-600'
                    }`}
                  >
                    {item.type === GiftItemInfoType.Delivery ? '배송' : '쿠폰'}
                  </span>
                  {/* 배송 카테고리 뱃지 */}
                  {item.type === GiftItemInfoType.Delivery &&
                    item.giftDeliveryCategory &&
                    typeof item.giftDeliveryCategory === 'object' && (
                      <span className="rounded bg-cyan-100 px-1.5 py-0.5 text-xs font-medium text-cyan-700">
                        {item.giftDeliveryCategory.name}
                      </span>
                    )}
                  {/* 하위분류 뱃지 */}
                  {item.type === GiftItemInfoType.Delivery && item.subCategory && (
                    <span className="rounded bg-teal-100 px-1.5 py-0.5 text-xs font-medium text-teal-700">
                      {item.subCategory}
                    </span>
                  )}
                  <span
                    className={`rounded px-1.5 py-0.5 text-xs ${
                      item.isEnabled
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {item.isEnabled ? '활성' : '비활성'}
                  </span>
                  {/* 선택형 뱃지 */}
                  {item.isSelectable && (
                    <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-xs font-medium text-indigo-600">
                      선택형
                      {item.choiceUnits && item.choiceUnits.length > 0 && (
                        <span className="ml-0.5">({item.choiceUnits.length})</span>
                      )}
                    </span>
                  )}
                  {/* 과세여부 뱃지 */}
                  <span
                    className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                      item.isTaxable !== false
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-emerald-100 text-emerald-700'
                    }`}
                  >
                    {item.isTaxable !== false ? '과세' : '비과세'}
                  </span>
                </div>
                <p
                  className={`mt-0.5 truncate font-medium ${
                    item.isEnabled ? '' : 'text-gray-400'
                  }`}
                >
                  {item.name}
                </p>
                <div className="flex flex-wrap items-center gap-x-2 text-sm">
                  {item.priceRegular ? (
                    <span className="text-gray-400 line-through">
                      {item.priceRegular?.toLocaleString()}원
                    </span>
                  ) : null}
                  <span className="font-medium text-red-500">
                    {item.priceRetail?.toLocaleString() || 0}원
                  </span>
                  <span className="text-xs text-gray-400">
                    (도매: {item.priceWhole?.toLocaleString()}원)
                  </span>
                  {getMarginRate(item) !== null && (
                    <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-bold text-red-600">
                      마진 {(item.priceRetail - item.priceWhole).toLocaleString()}원 (
                      {Math.round(getMarginRate(item) as number)}%)
                    </span>
                  )}
                  {topMarginIdSet.has(item._id) && (
                    <span
                      className="rounded bg-indigo-700 px-1.5 py-0.5 text-xs font-bold text-white"
                      title="같은 하위분류 중 마진율 최고"
                    >
                      하위분류 최고마진
                    </span>
                  )}
                  {item.tags && item.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {item.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="rounded-full bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-600"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* 선택유닛 목록 (선택형일 때) */}
              {item.isSelectable && item.choiceUnits && item.choiceUnits.length > 0 && (
                <div className="flex flex-shrink-0 items-center gap-3 border-l pl-4">
                  <span className="text-sm font-medium text-gray-500">선택유닛</span>
                  <div className="flex items-center gap-2">
                    {item.choiceUnits.map((unit) => (
                      <div
                        key={typeof unit === 'object' ? unit._id : unit}
                        className="flex items-center gap-2 rounded-lg bg-indigo-50 py-1 pl-1 pr-3"
                        title={typeof unit === 'object' ? unit.name : ''}
                      >
                        <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                          {typeof unit === 'object' && unit.image ? (
                            <img
                              src={fileBucketUrl(
                                typeof unit.image === 'object'
                                  ? unit.image.url
                                  : unit.image,
                                'raw',
                              )}
                              alt={unit.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                              No
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <span className="max-w-[100px] truncate text-xs font-medium text-gray-700">
                            {typeof unit === 'object' ? unit.name : unit}
                          </span>
                          {/* 가격 표시 */}
                          {typeof unit === 'object' &&
                            (unit.priceRegular || unit.priceWhole) && (
                              <div className="flex items-center gap-1 text-[10px]">
                                {unit.priceRegular ? (
                                  <span className="text-gray-600">
                                    {unit.priceRegular.toLocaleString()}원
                                  </span>
                                ) : null}
                                {unit.priceWhole ? (
                                  <span className="text-gray-400">
                                    (도매 {unit.priceWhole.toLocaleString()})
                                  </span>
                                ) : null}
                              </div>
                            )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-gray-400">→</div>
            </Link>
          ))}
        </div>
      );
    }

    return (
      <div className="center-box h-screen-15">
        <Loading />
      </div>
    );
  }, [
    status,
    filteredAndSortedData,
    searchQuery,
    typeFilter,
    statusFilter,
    taxFilter,
    brandFilter,
    deliveryCategoryFilter,
    subCategoryFilter,
    topMarginIdSet,
  ]);

  // 브랜드 일괄 활성/비활성 — 특정 브랜드를 골랐을 때만 노출, 현재 필터 결과(표시 중인 목록)에 적용
  const [bulkEnabling, setBulkEnabling] = useState(false);
  const selectedBrandName = useMemo(
    () => brandList.find((b) => b._id === brandFilter)?.name,
    [brandList, brandFilter],
  );
  const bulkTarget = useMemo(() => {
    if (brandFilter === 'all' || brandFilter === 'none') return null;
    const enabled = filteredAndSortedData.filter((item) => item.isEnabled);
    const disabled = filteredAndSortedData.filter((item) => !item.isEnabled);
    return { enabled, disabled };
  }, [brandFilter, filteredAndSortedData]);

  const handleBulkEnabled = async (isEnabled: boolean) => {
    if (!bulkTarget) return;
    const targets = isEnabled ? bulkTarget.disabled : bulkTarget.enabled;
    if (targets.length === 0) return;
    const label = isEnabled ? '활성' : '비활성';
    if (
      !confirm(
        `${selectedBrandName ?? '선택 브랜드'} 원상품 ${
          targets.length
        }개를 ${label}으로 바꿉니다.\n(현재 표시 중인 목록 기준)`,
      )
    ) {
      return;
    }
    setBulkEnabling(true);
    try {
      await bulkSetGiftItemInfoEnabled(
        targets.map((item) => item._id),
        isEnabled,
        sellerRole(),
      );
      await invalidateQueriesByUrlPart(queryClient, 'gift-item-info');
    } catch (error) {
      console.error(error);
      alert('일괄 변경에 실패했습니다.');
    } finally {
      setBulkEnabling(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {bulkEnabling && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white/70">
          <Loading />
          <p className="mt-3 text-sm font-medium text-gray-700">상태 변경 중…</p>
        </div>
      )}
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">원상품 관리</h1>
          <Link to="/gift-seller/item-info/new">
            <Button className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white">
              + 원상품 등록
            </Button>
          </Link>
        </div>

        {/* 통계 */}
        {status === 'success' && (
          <div className="mt-3 flex gap-4 text-sm">
            <span className="text-gray-600">
              전체 <span className="font-medium text-gray-900">{stats.total}</span>
            </span>
            <span className="text-blue-600">
              배송형 <span className="font-medium">{stats.delivery}</span>
            </span>
            <span className="text-purple-600">
              쿠폰형 <span className="font-medium">{stats.coupon}</span>
            </span>
            <span className="text-green-600">
              활성 <span className="font-medium">{stats.enabled}</span>
            </span>
            <span className="text-gray-500">
              비활성 <span className="font-medium">{stats.disabled}</span>
            </span>
          </div>
        )}
      </div>

      {/* 필터 및 검색 */}
      <div className="border-b bg-white px-6 py-3">
        <div className="flex flex-wrap items-center gap-3">
          {/* 검색 */}
          <div className="relative flex-1">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="상품명, 태그, 상품코드 검색..."
              className="w-full rounded-lg border px-3 py-2 pl-9 text-sm"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              🔍
            </span>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>

          {/* 브랜드 필터 */}
          <select
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm"
            disabled={brandListStatus !== 'success'}
          >
            <option value="all">전체 브랜드</option>
            <option value="none">브랜드 없음</option>
            {brandList.map((brand) => (
              <option key={brand._id} value={brand._id}>
                {brand.name}
              </option>
            ))}
          </select>

          {/* 타입 필터 */}
          <select
            value={typeFilter}
            onChange={(e) =>
              handleTypeFilterChange(e.target.value as GiftItemInfoTypeFilter)
            }
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 타입</option>
            <option value="Delivery">배송형</option>
            <option value="Coupon">쿠폰형</option>
          </select>

          {/* 배송 카테고리 필터 (배송형일 때만 노출) */}
          {typeFilter === 'Delivery' && (
            <select
              value={deliveryCategoryFilter}
              onChange={(e) => handleDeliveryCategoryFilterChange(e.target.value)}
              className="rounded-lg border px-3 py-2 text-sm"
              disabled={deliveryCategoryListStatus !== 'success'}
            >
              <option value="all">전체 배송 카테고리</option>
              <option value="none">카테고리 없음</option>
              {deliveryCategoryList.map((category) => (
                <option key={category._id} value={category._id}>
                  {category.name}
                </option>
              ))}
            </select>
          )}

          {/* 하위분류 필터 (배송형일 때만 노출) */}
          {typeFilter === 'Delivery' && (
            <select
              value={subCategoryFilter}
              onChange={(e) => setSubCategoryFilter(e.target.value)}
              className="rounded-lg border px-3 py-2 text-sm"
            >
              <option value="all">전체 하위분류</option>
              <option value="none">하위분류 없음</option>
              {availableSubCategories.map((sub) => (
                <option key={sub} value={sub}>
                  {sub}
                </option>
              ))}
            </select>
          )}

          {/* 활성화 필터 */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as GiftItemInfoStatusFilter)}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 상태</option>
            <option value="enabled">활성만</option>
            <option value="disabled">비활성만</option>
          </select>

          {/* 과세여부 필터 */}
          <select
            value={taxFilter}
            onChange={(e) => setTaxFilter(e.target.value as GiftItemInfoTaxFilter)}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 과세</option>
            <option value="taxable">과세만</option>
            <option value="taxFree">비과세만</option>
          </select>

          {/* 정렬 */}
          <select
            value={sortType}
            onChange={(e) => setSortType(e.target.value as GiftItemInfoSortType)}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="order">표시순서</option>
            <option value="latest">최신순</option>
            <option value="oldest">오래된순</option>
            <option value="name-asc">이름 ㄱ→ㅎ</option>
            <option value="name-desc">이름 ㅎ→ㄱ</option>
            <option value="price-asc">가격 낮은순</option>
            <option value="price-desc">가격 높은순</option>
          </select>
        </div>

        {/* 브랜드 일괄 활성/비활성 */}
        {status === 'success' && bulkTarget && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm">
            <span className="font-medium text-indigo-800">
              {selectedBrandName ?? '선택 브랜드'} 일괄 변경
            </span>
            <span className="text-xs text-gray-600">
              표시 중 {filteredAndSortedData.length}개 · 활성 {bulkTarget.enabled.length}{' '}
              · 비활성 {bulkTarget.disabled.length}
            </span>
            <div className="ml-auto flex gap-2">
              <button
                type="button"
                onClick={() => handleBulkEnabled(true)}
                disabled={bulkEnabling || bulkTarget.disabled.length === 0}
                className="rounded border border-green-600 bg-white px-3 py-1 text-xs font-medium text-green-700 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                비활성 {bulkTarget.disabled.length}개 → 활성
              </button>
              <button
                type="button"
                onClick={() => handleBulkEnabled(false)}
                disabled={bulkEnabling || bulkTarget.enabled.length === 0}
                className="rounded border border-gray-500 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
              >
                활성 {bulkTarget.enabled.length}개 → 비활성
              </button>
            </div>
          </div>
        )}

        {/* 검색 결과 수 */}
        {status === 'success' && (
          <div className="mt-2 text-xs text-gray-500">
            {filteredAndSortedData.length}개 표시
            {(searchQuery ||
              typeFilter !== 'all' ||
              statusFilter !== 'all' ||
              taxFilter !== 'all' ||
              brandFilter !== 'all' ||
              deliveryCategoryFilter !== 'all' ||
              subCategoryFilter !== 'all') &&
              ` (전체 ${stats.total}개 중)`}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">{itemListRender}</div>
    </div>
  );
};
